import React, { useEffect, useState } from "react"
import { useParams, Outlet, Navigate } from "react-router-dom"
import { useRegionId } from "@/shared/hooks/use-region-id"
import { useSession } from "@/features/auth/context/session.context"
import { authFetchJson } from "@/shared/lib/auth-fetch"

/**
 * Region layout — SPA architecture.
 *
 * Forces a remount on region change using the region slug from route params.
 * Validates the region slug and redirects if the region is invalid.
 */
function RegionLayout() {
  const { region } = useParams()
  const { regionId, regionSlug, isLoading, status } = useRegionId()
  const { profile, isLoading: sessionLoading } = useSession()
  const [resolvedSlug, setResolvedSlug] = useState<string | null>(null)
  const [isResolvingSlug, setIsResolvingSlug] = useState(false)

  const isQueryPending = status === "pending" && !!regionSlug

  useEffect(() => {
    if (isLoading || sessionLoading || isQueryPending) return
    if (regionId !== null) return // Valid region slug

    // Invalid region slug - resolve a fallback slug
    setIsResolvingSlug(true)
    const fetchFallbackSlug = async () => {
      try {
        const selectedRegionId = profile?.selectedRegion
        if (selectedRegionId) {
          const res = await authFetchJson<{ slug: string }>(`/regions/${selectedRegionId}`)
          if (res.success && res.data?.slug) {
            setResolvedSlug(res.data.slug)
            return
          }
        }

        // Fallback: fetch active regions and use the first one
        const regionsRes = await authFetchJson<any>('/regions/options?activeOnly=true')
        if (regionsRes.success) {
          const regions = (regionsRes.data as any)?.data ?? regionsRes.data
          if (Array.isArray(regions) && regions.length > 0) {
            setResolvedSlug(regions[0].slug)
            return
          }
        }
        setResolvedSlug("swannanoa-valley")
      } catch {
        setResolvedSlug("swannanoa-valley")
      } finally {
        setIsResolvingSlug(false)
      }
    }
    fetchFallbackSlug()
  }, [regionId, isLoading, sessionLoading, profile, isQueryPending])

  if (isLoading || sessionLoading || isResolvingSlug || isQueryPending) {
    return (
      <div className="flex h-screen w-screen items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  if (regionId === null) {
    const currentPath = window.location.pathname
    const currentSearch = window.location.search
    const segments = currentPath.split("/").filter(Boolean)
    // Replace the first segment (invalid region slug) with the resolved fallback slug
    segments[0] = resolvedSlug || "swannanoa-valley"
    const targetPath = `/${segments.join("/")}${currentSearch}`

    return <Navigate to={targetPath} replace />
  }

  return <React.Fragment key={region}><Outlet /></React.Fragment>
}

export default RegionLayout
